import type {
  AircraftBuildRequest,
  AircraftBuildResponse,
  AircraftCatalogResponse,
  AircraftParmsResponse,
  GltfDoc,
} from "../../api/aircraft";
import { apiClient } from "../../api/client";

export const X57_LOCAL_DEMO_PROJECT_ID = "00000000-0000-4000-8000-000000009009" as const;

export interface X57VspRequestOptions {
  signal?: AbortSignal;
}

export function vspProjectPath(projectId: string): string {
  if (!projectId.trim()) throw new Error("A Thread project id is required for VSP access");
  return `/thread/v1/projects/${encodeURIComponent(projectId)}/aero/vsp`;
}

export type X57VspPreviewRequest = Required<Pick<AircraftBuildRequest, "model_key" | "resolution">>
  & Pick<AircraftBuildRequest, "parm_overrides">;

export async function fetchVspModels(
  projectId: string,
  options: X57VspRequestOptions = {},
): Promise<AircraftCatalogResponse> {
  return apiClient<AircraftCatalogResponse>(`${vspProjectPath(projectId)}/models`, {
    signal: options.signal,
  });
}

export async function fetchVspParameters(
  projectId: string,
  modelKey: string,
  options: X57VspRequestOptions = {},
): Promise<AircraftParmsResponse> {
  return apiClient<AircraftParmsResponse>(`${vspProjectPath(projectId)}/parameters`, {
    method: "POST",
    body: { model_key: modelKey },
    signal: options.signal,
  });
}

export async function buildVspPreview(
  projectId: string,
  request: X57VspPreviewRequest,
  options: X57VspRequestOptions = {},
): Promise<AircraftBuildResponse> {
  return apiClient<AircraftBuildResponse>(`${vspProjectPath(projectId)}/previews`, {
    method: "POST",
    body: request,
    signal: options.signal,
  });
}

function canonicalArtifactClientPath(projectId: string, url: string, expectedArtifact: string): string {
  const path = url.startsWith("/api/") ? url.slice(4) : url;
  const expectedPrefix = `${vspProjectPath(projectId)}/previews/`;
  const remainder = path.startsWith(expectedPrefix) ? path.slice(expectedPrefix.length) : "";
  const [bundleKey, artifact, extra] = remainder.split("/");
  if (
    extra !== undefined
    || artifact !== expectedArtifact
    || !bundleKey
    || !/^[A-Za-z0-9._-]+$/.test(bundleKey)
  ) {
    throw new Error(`Refusing non-canonical VSP preview artifact URL: ${url}`);
  }
  return path;
}

export async function fetchVspPreviewModel(
  projectId: string,
  modelUrl: string,
  options: X57VspRequestOptions = {},
): Promise<GltfDoc> {
  return apiClient<GltfDoc>(canonicalArtifactClientPath(projectId, modelUrl, "model.gltf"), {
    signal: options.signal,
  });
}

async function fetchVspPreviewJson(
  projectId: string,
  artifactUrl: string,
  artifactName: "bezier.json" | "metadata.json",
  options: X57VspRequestOptions,
): Promise<unknown> {
  return apiClient<unknown>(canonicalArtifactClientPath(projectId, artifactUrl, artifactName), {
    signal: options.signal,
  });
}

export async function fetchVspPreviewSurfaces(
  projectId: string,
  surfaceUrl: string,
  options: X57VspRequestOptions = {},
): Promise<unknown> {
  return fetchVspPreviewJson(projectId, surfaceUrl, "bezier.json", options);
}

export async function fetchVspPreviewMetadata(
  projectId: string,
  metadataUrl: string,
  options: X57VspRequestOptions = {},
): Promise<unknown> {
  return fetchVspPreviewJson(projectId, metadataUrl, "metadata.json", options);
}
